/**
 * yake-set-multiplier.js — Yake Mall toptan set / minimum sipariş adedi sistemi (ürün sayfası).
 *
 * Veri: product-information bölümündeki <script data-yake-set-data> (yake-set-data.liquid).
 * Parser: window.ToserofSetParser (set-distribution-parser.js, theme.liquid'de klasik script).
 * Metinler: window.setMultiplierI18n (yake-set-i18n.liquid).
 *
 * Pebble mimarisi:
 *  - Sepete ekleme, ana ürün formunun submit olayı window'da capture aşamasında yakalanır
 *    (temanın document-capture delegasyonundan ÖNCE çalışır), tek istekle /cart/add.js items[] gönderilir.
 *  - Varyant değişiminde bölüm morph edilir; JSON ve bilgi kutusu sunucu tarafından yeniden çizildiği
 *    için burada yalnızca yeniden okunur/doldurulur.
 *  - Başarıda temanın CartAddEvent'i (cart:update) yayınlanır → sepet sayacı ve diğer bileşenler güncellenir.
 */
import { CartAddEvent, CartGroupedSections, ThemeEvents } from "@theme/events";

(() => {
  "use strict";

  const DEBUG = false;
  const log = (...a) => { if (DEBUG) console.log("[Yake Set]", ...a); };

  const T = (key, repl, fallback) => {
    const dict = window.setMultiplierI18n || {};
    let s = dict[key] != null ? dict[key] : fallback;
    s = String(s == null ? "" : s);
    if (repl) for (const k in repl) s = s.split("__" + k + "__").join(String(repl[k]));
    return s;
  };
  const esc = (str) => String(str == null ? "" : str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const norm = (s) => String(s == null ? "" : s).toUpperCase().replace(/[()]/g, "").trim();
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  const dataScript = document.querySelector("[data-yake-set-data]");
  if (!dataScript) return;
  const scope = dataScript.closest(".shopify-section") || document;

  const state = {
    data: null,
    mode: "none",          // none | std | multi | beden_seti
    dist: {},              // multi: {SIZE: qty}
    collapsed: null,       // multi collapsed to STD (compound/no-size)
    ranges: {},            // beden_seti: {RANGE: mult}
    stdMult: 0,
    minQty: 0,
    active: false,
    busy: false,
    description: null,     // TR açıklama (ertelemeli aramada doldurulur)
  };

  // ------------------------------------------------------------------ veri
  function readData() {
    const el = scope.querySelector("[data-yake-set-data]") || dataScript;
    try { state.data = JSON.parse(el.textContent); } catch (e) { state.data = null; }
    return state.data;
  }

  function variantSize(v) {
    const d = state.data;
    if (!d || !d.sizeOptionPosition) return "DEFAULT";
    return String((v.options || [])[d.sizeOptionPosition - 1] || "");
  }
  function variantColor(v) {
    const d = state.data;
    const out = [];
    (v.options || []).forEach((o, i) => { if (i !== d.sizeOptionPosition - 1) out.push(o); });
    return out.join(" / ");
  }
  function variantById(id) { return (state.data.variants || []).find((v) => String(v.id) === String(id)) || null; }
  function selectedVariantId() {
    const input = scope.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    if (input && input.value) return input.value;
    return String(state.data.selectedVariantId || "");
  }
  function selectedVariant() {
    return variantById(selectedVariantId()) || state.data.variants[0] || null;
  }
  function selectedColor() { const v = selectedVariant(); return v ? variantColor(v) : ""; }
  function availableSizes() { return state.data.sizes || []; }

  function findVariantForSize(size, color) {
    const target = norm(size);
    const vs = state.data.variants || [];
    if (target === "STD" || target === "DEFAULT" || !state.data.sizeOptionPosition) {
      const sel = selectedVariant();
      if (sel && (!color || variantColor(sel) === color)) return sel;
      return vs.find((v) => !color || variantColor(v) === color) || sel || vs[0] || null;
    }
    let found = vs.find((v) => norm(variantSize(v)) === target && (!color || variantColor(v) === color));
    if (found) return found;
    found = vs.find((v) => norm(variantSize(v)) === target);
    if (found) return found;
    found = vs.find((v) => { const s = norm(variantSize(v)); return s.includes("-") && s.split("-").map((x) => x.trim()).includes(target) && (!color || variantColor(v) === color); });
    return found || null;
  }

  // ------------------------------------------------------------------ parse
  function applyParse(desc) {
    const P = window.ToserofSetParser;
    if (!P) { console.error("[Yake Set] parser yok"); return false; }
    const r = P.parse(desc || "", availableSizes(), state.data.title) || { found: false };
    state.minQty = r.minQty || 0;
    state.mode = "none";
    state.dist = {}; state.collapsed = null; state.ranges = {}; state.stdMult = 0;
    if (r.found && r.type === "beden_seti") {
      state.mode = "beden_seti"; state.ranges = r.distributions || {};
    } else if (r.found && r.type === "std") {
      state.mode = "std"; state.stdMult = r.multiplier || 1;
    } else if (r.found && r.type === "multi_size_set") {
      const d = r.distribution || {};
      const keys = Object.keys(d);
      if (keys.length > 1 && availableSizes().length <= 1) {
        state.mode = "std"; state.stdMult = keys.reduce((s, k) => s + d[k], 0); state.collapsed = { ...d };
      } else if (keys.length === 1 && availableSizes().length <= 1 && (!state.data.sizeOptionPosition || norm(keys[0]) !== norm(availableSizes()[0] || ""))) {
        state.mode = "std"; state.stdMult = d[keys[0]]; state.collapsed = { ...d };
      } else {
        state.mode = "multi"; state.dist = d;
      }
    }
    log("parse", r, "mode", state.mode, "minQty", state.minQty);
    return state.mode !== "none" || state.minQty > 0;
  }

  function perSet() {
    if (state.mode === "std") return state.stdMult;
    if (state.mode === "multi") return Object.values(state.dist).reduce((s, q) => s + q, 0);
    if (state.mode === "beden_seti") { const m = currentRangeMultiplier(); return m || 1; }
    return 1;
  }
  function minSets() {
    if (!state.minQty) return 1;
    return Math.max(1, Math.ceil(state.minQty / perSet()));
  }

  function isSizeInRange(size, range) {
    const cs = norm(size), cr = norm(range);
    if (cs === cr) return true;
    const rp = cr.split("-");
    if (rp.length !== 2) return false;
    const a = parseInt(rp[0], 10), b = parseInt(rp[1], 10);
    if (cs.includes("-")) { const sp = cs.split("-"); return sp.length === 2 && parseInt(sp[0], 10) === a && parseInt(sp[1], 10) === b; }
    const n = parseInt(cs, 10);
    return !isNaN(n) && !isNaN(a) && !isNaN(b) && n >= a && n <= b;
  }
  function currentRange() {
    const sel = selectedVariant();
    if (!sel) return null;
    const size = variantSize(sel);
    for (const [range, mult] of Object.entries(state.ranges)) if (isSizeInRange(size, range)) return { range, mult };
    const first = Object.entries(state.ranges)[0];
    if (first) return { range: norm(size) || first[0], mult: first[1] };
    return null;
  }
  function currentRangeMultiplier() { const r = currentRange(); return r ? r.mult : 0; }

  // ------------------------------------------------------------------ stok / limit
  function stockOf(v) { if (!v) return 0; if (!v.available) return 0; return v.tracked ? Math.max(0, v.stock | 0) : Infinity; }

  function analyze() {
    // → { maxSets, limiting, missing[], lines[{variant,size,perSetQty}] }
    const color = selectedColor();
    if (state.mode === "std") {
      const v = selectedVariant();
      const stock = stockOf(v);
      return { maxSets: v ? Math.floor(stock / state.stdMult) : 0, limiting: T("lfStockMult", { STOCK: stock === Infinity ? "∞" : stock, M: state.stdMult }, "__STOCK__ stok / __M__ çarpan"), missing: [], lines: v ? [{ variant: v, size: "STD", perSetQty: state.stdMult }] : [] };
    }
    if (state.mode === "beden_seti") {
      const v = selectedVariant();
      const r = currentRange();
      const stock = stockOf(v);
      const mult = r ? r.mult : 1;
      return { maxSets: v ? Math.floor(stock / mult) : 0, limiting: "", missing: [], lines: v ? [{ variant: v, size: r ? r.range : variantSize(v), perSetQty: mult }] : [] };
    }
    if (state.mode === "multi") {
      let maxSets = Infinity, limiting = "", missing = [];
      const lines = [];
      for (const [size, q] of Object.entries(state.dist)) {
        const v = findVariantForSize(size, color);
        if (!v) { missing.push(size); continue; }
        const stock = stockOf(v);
        const possible = Math.floor(stock / q);
        if (possible < maxSets) { maxSets = possible; limiting = T("lfSizeStock", { SIZE: size, N: stock === Infinity ? "∞" : stock }, "__SIZE__ bedeni (__N__ stok)"); }
        lines.push({ variant: v, size, perSetQty: q });
      }
      if (missing.length) return { maxSets: 0, limiting: T("lfSizeMissing", { SIZE: missing.join(", ") }, "__SIZE__ bedeni bulunamadı"), missing, lines };
      return { maxSets: maxSets === Infinity ? Infinity : maxSets, limiting, missing, lines };
    }
    // sadece min adet
    const v = selectedVariant();
    return { maxSets: v ? stockOf(v) : 0, limiting: "", missing: [], lines: v ? [{ variant: v, size: "", perSetQty: 1 }] : [] };
  }

  function qtyInput() { return scope.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]') || scope.querySelector(".buy-buttons-block input[name=\"quantity\"]"); }
  function currentQty() { const i = qtyInput(); const v = parseInt(i && i.value, 10); return isNaN(v) || v < 1 ? 1 : v; }

  function applyLimits() {
    const input = qtyInput();
    if (!input) return;
    const a = analyze();
    const min = minSets();
    const max = a.maxSets === Infinity ? null : Math.max(0, a.maxSets);
    input.min = String(min);
    if (max === null) input.removeAttribute("max"); else input.max = String(Math.max(min, max));
    input.step = "1";
    let v = parseInt(input.value, 10);
    if (isNaN(v) || v < min) v = min;
    if (max !== null && max > 0 && v > max) { v = max; }
    if (max !== null && max === 0) {
      input.value = String(min);
      input.disabled = true;
      input.classList.add("yake-set-qty--blocked");
    } else {
      input.disabled = false;
      input.classList.remove("yake-set-qty--blocked");
      input.value = String(v);
    }
    input.dispatchEvent(new Event("input", { bubbles: true }));
    return a;
  }

  // ------------------------------------------------------------------ UI
  function infoBox() { return scope.querySelector("[data-yake-set-info]"); }
  function totalBox() { return scope.querySelector("[data-yake-set-total]"); }

  function renderInfo() {
    const box = infoBox();
    if (!box) return;
    const textEl = box.querySelector("[data-yake-set-text]");
    const detEl = box.querySelector("[data-yake-set-details]");
    if (state.mode === "none" && state.minQty) {
      textEl.innerHTML = "<strong>" + esc(T("badgeMin", null, "Minimum sipariş:")) + "</strong> " + esc(T("textMin", { N: state.minQty }, "Bu ürün en az __N__ adet sipariş edilebilir."));
      detEl.innerHTML = "";
      box.hidden = false;
      return;
    }
    if (state.mode === "beden_seti") {
      textEl.innerHTML = "<strong>" + esc(T("badgeBedenSeti", null, "Beden Seti:")) + "</strong> " + esc(T("textBedenSetiAuto", null, "Seçtiğiniz bedene göre otomatik olarak farklı miktarlarda ürün sepetinize eklenecektir."));
    } else if (state.mode === "std") {
      textEl.innerHTML = "<strong>" + esc(T("badgeSet", null, "Set Ürün:")) + "</strong> " + esc(T("perOrderStd", { N: state.stdMult }, "Her 1 adet sipariş için __N__ adet ürün sepetinize eklenecektir."));
    } else if (state.mode === "multi") {
      textEl.innerHTML = "<strong>" + esc(T("badgeSet", null, "Set Ürün:")) + "</strong> " + esc(T("perOrderMulti", { N: perSet() }, "Her 1 adet için __N__ farklı beden ürünü sepetinize eklenecektir."));
    }
    box.hidden = false;
    renderSelection();
  }

  function renderSelection() {
    const box = infoBox();
    if (!box) return;
    const detEl = box.querySelector("[data-yake-set-details]");
    const q = currentQty();
    const a = analyze();
    const maxTxt = (sets, items) => T("maxAddable", { SETS: sets === Infinity ? "∞" : sets, ITEMS: sets === Infinity ? "∞" : items }, "Maksimum eklenebilir: __SETS__ set (__ITEMS__ adet)");
    let html = "";
    if (state.mode === "beden_seti") {
      let rows = '<ul class="yake-set-info__rows">';
      for (const [range, mult] of Object.entries(state.ranges)) {
        rows += "<li>" + T("rowRangeHtml", { RANGE: '<span class="yake-set-info__range">' + esc(range) + "</span>", N: mult }, "__RANGE__ seçerseniz → <strong>__N__ adet</strong> ürün eklenecek") + "</li>";
      }
      rows += "</ul>";
      const r = currentRange();
      if (r) {
        rows += '<div class="yake-set-info__selection"><div>' + T("selectionHtml", { RANGE: esc(r.range), Q: q, M: r.mult, TOTAL: q * r.mult }, '<strong>Seçiminiz:</strong> __RANGE__ → __Q__ × __M__ = <span class="yake-set-info__selection-total">__TOTAL__ adet</span> sepete eklenecek') + "</div>" +
          '<div class="yake-set-info__selection-max">' + esc(maxTxt(a.maxSets, a.maxSets === Infinity ? Infinity : a.maxSets * r.mult)) + "</div></div>";
      }
      html = rows;
    } else if (state.mode === "std") {
      html = '<p class="yake-set-info__hint">' + esc(T("textSet", null, "Bu ürün set halinde satılmaktadır.")) + "</p>";
      if (state.collapsed) {
        html += '<p class="yake-set-info__distribution"><strong>' + esc(T("distLabel", null, "Dağılım:")) + "</strong> " + Object.entries(state.collapsed).map(([s, n]) => esc(T("distPart", { SIZE: s, N: n }, "__SIZE__ (__N__ adet)"))).join(", ") + "</p>";
      }
    } else if (state.mode === "multi") {
      html = '<p class="yake-set-info__distribution"><strong>' + esc(T("distLabel", null, "Dağılım:")) + "</strong> " + Object.entries(state.dist).map(([s, n]) => esc(T("distPart", { SIZE: s, N: n }, "__SIZE__ (__N__ adet)"))).join(", ") + "</p>";
      if (a.missing.length) {
        html += '<div class="yake-set-info__selection yake-set-info__selection--error"><div><strong>' + esc(T("cannotAdd", null, "⚠ Ekleme Yapılamaz")) + "</strong></div><ul class=\"yake-set-info__error-list\"><li>" + esc(T("errMissingSizes", { LIST: a.missing.join(", ") }, "Eksik bedenler: __LIST__")) + "</li></ul></div>";
      }
    }
    if (state.minQty) {
      html += '<div class="yake-set-info__min">' + esc(T("minLine", { N: state.minQty, SETS: minSets() }, "Minimum sipariş: __N__ adet (__SETS__ set)")) + "</div>";
    }
    detEl.innerHTML = html;
  }

  function renderTotal() {
    const box = totalBox();
    if (!box) return;
    const ps = perSet();
    const unit = (state.data.priceCents || 0) / 100;
    if (state.mode === "none" || ps < 2 || !unit) { box.hidden = true; box.innerHTML = ""; return; }
    const total = unit * ps;
    let tr;
    try { tr = total.toLocaleString("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2, maximumFractionDigits: 2 }); } catch (e) { tr = "₺" + total.toFixed(2); }
    box.innerHTML = esc(T("setTotalLabel", null, "Set toplamı")) + ' <strong class="glc-money yake-set-total__money" data-yake-tl="' + total.toFixed(2) + '">' + esc(tr) + "</strong> <span class=\"yake-set-total__hint\">" + esc(T("setTotalHint", { N: ps }, "(__N__ adet × birim fiyat)")) + "</span>";
    box.hidden = false;
  }

  // Temanın "Stokta yalnızca N adet kaldı" bloğu seçili varyantı gösterir; set ürünlerinde set bazlı stok yazılır.
  const INV_ICONS = {
    ok: '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    low: '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M10 6v5M10 13.5v.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    none: '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  };
  let invObserver = null;
  function renderInventory() {
    const inv = scope.querySelector("product-inventory");
    if (!inv || state.mode === "none") return;
    const a = analyze();
    let status, cls, text;
    if (a.missing.length || a.maxSets === 0) {
      status = "none"; cls = "notification--out-of-stock";
      text = T("invNone", { X: a.limiting || a.missing.join(", ") }, "Set için yeterli stok yok (__X__)");
    } else if (a.maxSets === Infinity) {
      status = "ok"; cls = "notification--success";
      text = T("invUnlimited", null, "Stokta var");
    } else {
      const low = a.maxSets <= 5;
      status = low ? "low" : "ok"; cls = low ? "notification--error" : "notification--success";
      text = T("invSets", { N: a.maxSets, ITEMS: a.maxSets * perSet() }, "Stokta __N__ set var (__ITEMS__ adet)");
    }
    const html = '<span class="product-inventory__status ' + cls + '" data-yake-inv="1"><span class="inline-flex icon product-inventory__icon product-inventory__icon-' + (status === "ok" ? "in_stock" : status === "low" ? "low" : "out_of_stock") + '">' + INV_ICONS[status] + '</span><span class="product-inventory__text" role="status">' + esc(text) + "</span></span>";
    if (inv.innerHTML !== html) inv.innerHTML = html;
    if (!invObserver && window.MutationObserver) {
      invObserver = new MutationObserver(() => { if (!inv.querySelector("[data-yake-inv]")) setTimeout(renderInventory, 30); });
      invObserver.observe(inv, { childList: true });
    }
  }

  function renderQtyLabel() {
    const input = qtyInput();
    if (!input) return;
    const row = input.closest(".product-form-buttons");
    const wrap = row ? row.parentElement : (input.closest("quantity-selector-component") || input).parentElement;
    const anchor = row || input.closest("quantity-selector-component") || input;
    if (!wrap) return;
    let label = wrap.querySelector(":scope > .yake-set-qty-label");
    if (state.mode === "none") { if (label) label.remove(); return; }
    if (!label) {
      label = document.createElement("div");
      label.className = "yake-set-qty-label";
      label.innerHTML = '<span class="yake-set-qty-label__text"></span><span class="yake-set-qty-label__total" aria-live="polite"></span>';
      wrap.insertBefore(label, anchor);
    }
    label.querySelector(".yake-set-qty-label__text").textContent = T("qtyLabel", null, "Set sayısı");
    const total = currentQty() * perSet();
    label.querySelector(".yake-set-qty-label__total").textContent = total > 0 ? T("liveTotal", { N: total }, "→ __N__ adet") : "";
  }

  function ensureStyles() {
    if (document.getElementById("yake-set-style")) return;
    const st = document.createElement("style");
    st.id = "yake-set-style";
    const name = String(state.data.sizeOptionName || "").toLowerCase().replace(/"/g, "");
    let css = "";
    if (name && state.mode !== "none") {
      if (state.mode === "beden_seti") {
        css += '.variant-option[data-option-name="' + name + '"] .variant-option__label-text::before{content:"' + T("paketPrefix", null, "Paket: ").replace(/"/g, "") + '";font-weight:500}';
      } else {
        css += '.variant-option[data-option-name="' + name + '"]{display:none !important}';
      }
    }
    if (state.mode !== "none") {
      css += '.product-information--main [ref="acceleratedCheckoutButtonContainer"],.product-information--main .shopify-payment-button,.product-information--main .accelerated-checkout{display:none !important}';
    }
    st.textContent = css;
    document.head.appendChild(st);
  }

  function cleanDescription() {
    const re = /(?:<(?:strong|b)>)?\s*(?:Set\s+İçerik\s+Bilgisi|Set\s+Icerik\s+Bilgisi|Seri\s+İçerik\s+Bilgisi|Seri\s+Icerik\s+Bilgisi|Beden\s+Seti|Dağılım|Paket\s+Yapısı|Paket\s+İçeriği|Min(?:imum)?\.?\s+(?:Sipariş\s+)?Adedi?)\s*:\s*(?:<\/(?:strong|b)>)?\s*[^<\n]{0,120}/gi;
    document.querySelectorAll(".product-description-content, .product-description, [data-yake-description]").forEach((el) => {
      const before = el.innerHTML;
      const after = before.replace(re, "");
      if (after !== before) el.innerHTML = after;
    });
  }

  // ------------------------------------------------------------------ uyarı / overlay / buton
  function warn(message) {
    const old = document.querySelector(".yake-set-warning");
    if (old) old.remove();
    const w = document.createElement("div");
    w.className = "yake-set-toast yake-set-warning";
    w.innerHTML = '<span class="yake-set-toast__icon" aria-hidden="true">!</span><span class="yake-set-toast__message"></span>';
    w.querySelector(".yake-set-toast__message").textContent = message;
    document.body.appendChild(w);
    setTimeout(() => { w.classList.add("yake-set-toast--leaving"); setTimeout(() => w.remove(), 300); }, 3200);
  }

  function overlay() {
    let o = document.getElementById("yakeSetOverlay");
    if (o) return o;
    o = document.createElement("div");
    o.id = "yakeSetOverlay";
    o.className = "yake-set-overlay";
    o.setAttribute("role", "status");
    o.setAttribute("aria-live", "polite");
    o.innerHTML = '<div class="yake-set-overlay__card"><div class="yake-set-overlay__spinner" aria-hidden="true"></div><div class="yake-set-overlay__check" aria-hidden="true">✓</div><div class="yake-set-overlay__message"></div><div class="yake-set-overlay__sub"></div><a class="yake-set-overlay__link btn btn--primary btn--small" hidden></a></div>';
    document.body.appendChild(o);
    return o;
  }
  function showOverlay(message, sub) {
    const o = overlay();
    o.classList.remove("yake-set-overlay--success", "yake-set-overlay--error");
    o.querySelector(".yake-set-overlay__message").textContent = message || T("overlayAdding", null, "Ürünler sepetinize ekleniyor…");
    o.querySelector(".yake-set-overlay__sub").textContent = sub || "";
    o.querySelector(".yake-set-overlay__link").hidden = true;
    void o.offsetHeight;
    o.classList.add("is-visible");
    clearTimeout(o._failsafe);
    o._failsafe = setTimeout(() => o.classList.remove("is-visible"), 15000);
  }
  function overlayResult(ok, message, sub) {
    const o = overlay();
    o.classList.remove("yake-set-overlay--success", "yake-set-overlay--error");
    o.classList.add(ok ? "yake-set-overlay--success" : "yake-set-overlay--error");
    o.querySelector(".yake-set-overlay__check").textContent = ok ? "✓" : "!";
    o.querySelector(".yake-set-overlay__message").textContent = message || (ok ? T("overlayAdded", null, "Sepete eklendi") : T("overlayFailed", null, "Eklenemedi"));
    o.querySelector(".yake-set-overlay__sub").textContent = sub || "";
    const link = o.querySelector(".yake-set-overlay__link");
    if (ok && window.FoxTheme && window.FoxTheme.routes) {
      link.href = window.FoxTheme.routes.cart_url || "/cart";
      link.textContent = T("viewCart", null, "Sepete git");
      link.hidden = false;
    }
    clearTimeout(o._hide); clearTimeout(o._failsafe);
    o._hide = setTimeout(() => o.classList.remove("is-visible"), ok ? 2600 : 3500);
    o.onclick = (e) => { if (e.target === o) { clearTimeout(o._hide); o.classList.remove("is-visible"); } };
  }
  function hideOverlay() { const o = document.getElementById("yakeSetOverlay"); if (o) { clearTimeout(o._hide); clearTimeout(o._failsafe); o.classList.remove("is-visible"); } }

  function addButton() { return scope.querySelector('.buy-buttons-block button[type="submit"][name="add"]'); }
  function buttonState(st, message) {
    const btn = addButton();
    if (!btn) return;
    if (!btn.dataset.yakeOriginal) btn.dataset.yakeOriginal = btn.innerHTML;
    const setLabel = (txt) => { const t = btn.querySelector(".add-to-cart-text__content"); const i = btn.querySelector(".btn__icon-text"); if (t) t.textContent = txt; if (i) i.textContent = txt; if (!t && !i) btn.textContent = txt; };
    clearTimeout(btn._yakeTimer);
    btn.classList.remove("yake-set-btn--loading", "yake-set-btn--success", "yake-set-btn--error");
    if (st === "loading") { btn.classList.add("yake-set-btn--loading"); btn.disabled = true; btn.setAttribute("aria-busy", "true"); setLabel(message || T("btnAdding", null, "Ekleniyor…")); }
    else if (st === "success") { btn.disabled = false; btn.removeAttribute("aria-busy"); btn.classList.add("yake-set-btn--success"); setLabel(message); btn._yakeTimer = setTimeout(() => { btn.classList.remove("yake-set-btn--success"); btn.innerHTML = btn.dataset.yakeOriginal; }, 2500); }
    else if (st === "error") { btn.disabled = false; btn.removeAttribute("aria-busy"); btn.classList.add("yake-set-btn--error"); setLabel(message || T("overlayFailed", null, "Eklenemedi")); btn._yakeTimer = setTimeout(() => { btn.classList.remove("yake-set-btn--error"); btn.innerHTML = btn.dataset.yakeOriginal; }, 3000); }
    else { btn.disabled = false; btn.removeAttribute("aria-busy"); btn.innerHTML = btn.dataset.yakeOriginal; }
  }

  // ------------------------------------------------------------------ sepete ekleme
  function gatherSections() {
    const sections = [];
    try { document.dispatchEvent(new CartGroupedSections(sections)); } catch (e) {}
    return [...new Set(sections)];
  }

  async function addSet(form) {
    if (state.busy) return;
    const q = currentQty();
    const a = analyze();
    const ms = minSets();
    if (q < ms) {
      const inp = qtyInput(); if (inp) inp.value = String(ms);
      warn(T("warnMin", { N: state.minQty, SETS: ms }, "Bu ürün için minimum sipariş __N__ adettir (__SETS__ set)."));
      return;
    }
    if (a.missing.length) { warn(T("warnIntegrity", null, "Set bütünlüğü korunamıyor! Eksik bedenler var.")); return; }
    if (a.maxSets !== Infinity && q > a.maxSets) {
      const ps = perSet();
      warn(a.maxSets > 0 ? T("warnMaxSet", { N: a.maxSets, ITEMS: a.maxSets * ps }, "Maksimum __N__ set (__ITEMS__ adet) eklenebilir!") : T("warnNoStock", null, "Stok yetersiz! Bu ürün sepete eklenemez."));
      return;
    }
    const items = a.lines.map((l) => ({ id: l.variant.id, quantity: l.perSetQty * q })).filter((i) => i.quantity > 0);
    if (!items.length) { warn(T("lfNoVariant", null, "Varyant bulunamadı")); return; }
    const totalUnits = items.reduce((s, i) => s + i.quantity, 0);

    state.busy = true;
    buttonState("loading");
    showOverlay(T("overlayAdding", null, "Ürünler sepetinize ekleniyor…"));
    const sections = gatherSections();
    const body = { items };
    if (sections.length) { body.sections = sections.join(","); body.sections_url = window.location.pathname; }
    let response = null;
    try {
      const res = await fetch((window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_add_url) || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify(body),
      });
      response = await res.json();
      if (!res.ok || (response && response.status && response.status >= 400)) {
        const msg = (response && (response.description || response.message)) || T("overlayFailed", null, "Eklenemedi");
        buttonState("error");
        overlayResult(false, T("overlayFailed", null, "Eklenemedi"), msg);
        return;
      }
      buttonState("success", T("btnAdded", { N: totalUnits }, "Sepete Eklendi ✓ (__N__ adet)"));
      overlayResult(true, T("overlayAdded", null, "Sepete eklendi"), T("overlayAddedSub", { N: totalUnits }, "__N__ adet ürün sepetinize eklendi"));
      const target = form.closest("product-form-component") || document;
      target.dispatchEvent(new CartAddEvent(response, form.id || "", { source: "yake-set", itemCount: totalUnits, productId: String(state.data.productId), variantId: "multiple", sections: response.sections }));
      document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: response } }));
      setTimeout(() => applyAll(), 400);
    } catch (e) {
      console.error("[Yake Set] ekleme hatası", e);
      buttonState("error");
      overlayResult(false, T("overlayFailed", null, "Eklenemedi"), T("overlayFailedSub", null, "Set bütünlüğü korunamadı"));
    } finally {
      state.busy = false;
    }
  }

  function onSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!scope.contains(form) || form.getAttribute("data-type") !== "add-to-cart-form") return;
    if (form.closest("quick-add-component")) return;
    if (!state.active) return;
    if (state.mode === "none") {
      // sadece minimum adet: kural sağlanıyorsa temanın normal akışı devam eder
      const q = currentQty();
      if (state.minQty && q < state.minQty) {
        e.preventDefault(); e.stopImmediatePropagation();
        const inp = qtyInput(); if (inp) inp.value = String(state.minQty);
        warn(T("warnMin", { N: state.minQty, SETS: state.minQty }, "Bu ürün için minimum sipariş __N__ adettir (__SETS__ set)."));
      }
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    addSet(form);
  }

  // ------------------------------------------------------------------ yaşam döngüsü
  function applyAll() {
    ensureStyles();
    applyLimits();
    renderInfo();
    renderTotal();
    renderQtyLabel();
    renderInventory();
    cleanDescription();
  }

  const onQtyChange = debounce(() => { if (!state.active) return; const inp = qtyInput(); if (!inp) return; const min = minSets(); let v = parseInt(inp.value, 10); if (isNaN(v) || v < min) { v = min; inp.value = String(v); } renderSelection(); renderQtyLabel(); }, 150);
  const onVariantUpdate = debounce(() => {
    if (!state.active) return;
    readData();
    if (state.description) { /* ertelemeli: TR açıklama sabit */ } else { applyParse(state.data.description); }
    applyAll();
  }, 60);

  function activate() {
    state.active = true;
    applyAll();
    window.addEventListener("submit", onSubmit, true);
    scope.addEventListener("input", (e) => { if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) onQtyChange(); });
    scope.addEventListener("change", (e) => { if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) onQtyChange(); });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, (e) => { if (scope.contains(e.target)) onQtyChange(); });
    document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
    document.addEventListener(ThemeEvents.variantChanged, onVariantUpdate);
    log("aktif", state.mode, "minQty", state.minQty);
  }

  function parserReady() {
    if (window.ToserofSetParser) return Promise.resolve();
    return new Promise((resolve) => {
      let tries = 0;
      const tick = () => { if (window.ToserofSetParser || tries++ > 100) resolve(); else setTimeout(tick, 50); };
      tick();
    });
  }

  async function init() {
    if (!readData()) return;
    await parserReady();
    if (state.data.deferred) {
      try {
        const r = await fetch("/products/" + state.data.handle + ".js", { headers: { Accept: "application/json" } });
        if (r.ok) { const p = await r.json(); state.description = (p && p.description) || ""; }
      } catch (e) { log("ertelemeli açıklama alınamadı", e); }
    }
    const desc = state.description != null ? state.description : state.data.description;
    if (!applyParse(desc)) { log("set/min adet bilgisi yok"); return; }
    activate();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})();
